import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Loader2, Search } from "lucide-react";

export default function MedicineAutocomplete({
  value, onChange, onSelect, endpoint = "/medicines", placeholder = "Type to search…", testId,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const query = (q) => {
    clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(endpoint, { params: { q, limit: 12 } });
        setResults(r.data);
        setOpen(true);
      } catch {} finally { setLoading(false); }
    }, 220);
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          data-testid={testId}
          value={value}
          onChange={(e) => { onChange(e.target.value); query(e.target.value); }}
          onFocus={() => value?.length >= 2 && results.length && setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30 focus:border-[#1E5AA8]"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg" data-notranslate>
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { onSelect(r); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 hover:bg-[#EBF3FB] transition-colors border-b border-slate-50 last:border-0"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-slate-800 font-medium">{r.name}</span>
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {r.category || ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
